/**
 * IncassoStatsBar
 *
 * Statistics bar shown above the incasso kanban.
 * Displays: total outstanding amount, active cases, payment plan EUR,
 * legal procedure EUR, snoozed count + EUR, SLA warnings.
 * KPIs are clickable to filter the view.
 */

import { cn } from '@/lib/utils';

interface IncassoStatsBarProps {
  totalUnpaidRent: number | null;
  totalOutstanding: number;
  activeCases: number;
  snoozedCases: number;
  paymentPlanAmount: number;
  legalProcedureAmount: number;
  snoozedAmount: number;
  incassoViaDerdeAmount: number;
  onPaymentPlanClick?: () => void;
  onLegalProcedureClick?: () => void;
  onSnoozedClick?: () => void;
  onIncassoViaDerdeClick?: () => void;
}

const formatEuro = (amount: number): string => {
  return new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

interface StatItemProps {
  label: string;
  value: React.ReactNode;
  className?: string;
  valueClassName?: string;
  onClick?: () => void;
}

const StatItem = ({ label, value, className, valueClassName, onClick }: StatItemProps) => {
  const content = (
    <>
      <span className={cn('text-2xl font-bold text-neutral-900', valueClassName)}>{value}</span>
      <span className="text-xs text-neutral-secondary-mid uppercase tracking-wide mt-0.5">
        {label}
      </span>
    </>
  );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'flex flex-col items-center px-6 rounded-md py-1 transition-colors',
          'hover:bg-neutral-100 cursor-pointer',
          className,
        )}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={cn('flex flex-col items-center px-6', className)}>
      {content}
    </div>
  );
};

const Divider = () => (
  <div className="h-10 w-px bg-neutral-200 shrink-0" aria-hidden />
);

export const IncassoStatsBar = ({
  totalUnpaidRent,
  totalOutstanding,
  activeCases,
  snoozedCases,
  paymentPlanAmount,
  legalProcedureAmount,
  snoozedAmount,
  incassoViaDerdeAmount,
  onPaymentPlanClick,
  onLegalProcedureClick,
  onSnoozedClick,
  onIncassoViaDerdeClick,
}: IncassoStatsBarProps) => {
  return (
    <div className="flex items-center justify-center bg-white border border-neutral-200 rounded-lg py-3 px-4">
      <StatItem
        label="Openstaande klantenfacturen"
        value={totalUnpaidRent !== null ? formatEuro(totalUnpaidRent) : '—'}
      />

      <Divider />

      <StatItem
        label="Openstaande klantenfacturen minstens 15 dagen vervallen"
        value={formatEuro(totalOutstanding)}
      />

      <Divider />

      <StatItem
        label="Actieve dossiers"
        value={activeCases}
      />

      <Divider />

      <StatItem
        label="Afbetalingsplan"
        value={formatEuro(paymentPlanAmount)}
        valueClassName="text-blue-700"
        onClick={onPaymentPlanClick}
      />

      <Divider />

      <StatItem
        label="Vredegerecht"
        value={formatEuro(legalProcedureAmount)}
        valueClassName="text-red-700"
        onClick={onLegalProcedureClick}
      />

      <Divider />

      <StatItem
        label="Derde / deurwaarder"
        value={formatEuro(incassoViaDerdeAmount)}
        valueClassName="text-rose-700"
        onClick={onIncassoViaDerdeClick}
      />

      <Divider />

      <StatItem
        label="Gesnoozed"
        value={
          <span>
            {snoozedCases} <span className="text-lg font-normal text-neutral-500">({formatEuro(snoozedAmount)})</span>
          </span>
        }
        onClick={onSnoozedClick}
      />
    </div>
  );
};
